import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { TmsRouteType } from '@/app/constants/tmsRouteType'
import { getTmsRouteTypeName } from '@/app/services/tms/tmsRouteTypeNameResolver'
import { Dropdown } from '@/ui/form/controls/Dropdown'
import { Checkbox } from '@/ui/form/primitives/checkbox'
import { faTrash, FaIcon } from '@/lib/fa'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import TmsDateTimeInput from './TmsDateTimeInput'
import { TmsSourceFilter, useTmsCountryOptions } from './TmsSourceFilter'
import { TmsLoadsEditorState } from './useTmsLoadsEditor'

// Tło wiersza jak WPF TmsPartDataToColorConverter.
export const getRowBackground = (routeType: TmsRouteType) => {
  switch (routeType) {
    case TmsRouteType.Cabotage:
    case TmsRouteType.CabotageEmpty:
      return 'rgb(243, 218, 212)'
    case TmsRouteType.CrossTrade:
    case TmsRouteType.CrossTradeBilateral:
    case TmsRouteType.CrossTradeWrong:
    case TmsRouteType.CrossTradeBilateralEmpty:
    case TmsRouteType.CrossTradeWrongEmpty:
      return 'rgb(255, 238, 180)'
    default:
      return 'rgb(221, 235, 230)'
  }
}

const getColumns = (t: (key: string) => string): TableHeaderColumn[] => [
  { title: t('tmsDownload.docNumber'), key: 'docNumber', type: 'string', sort: false },
  { title: t('tmsDownload.loadingDate'), key: 'loadingDate', type: 'string', sort: false },
  { title: t('tmsDownload.loadingCountry'), key: 'loadingCountry', type: 'string', sort: false },
  { title: t('tmsDownload.unloadingDate'), key: 'unloadingDate', type: 'string', sort: false },
  { title: t('tmsDownload.unloadingCountry'), key: 'unloadingCountry', type: 'string', sort: false },
  { title: t('tmsDownload.vehicle'), key: 'vehicle', type: 'string', sort: false },
  { title: t('tmsDownload.routeType'), key: 'routeType', type: 'string', sort: false },
  { title: '', key: 'actions', type: 'string', sort: false },
]

interface TmsLoadsEditorProps {
  editor: TmsLoadsEditorState
  companyId: string
  driverName?: string
}

const TmsLoadsEditor: React.FC<TmsLoadsEditorProps> = ({
  editor,
  companyId,
  driverName,
}) => {
  const { t } = useLocalize()
  const { countries, countryOptions } = useTmsCountryOptions(companyId)

  const { items, isLoading, mode } = editor
  const allChecked = items.length > 0 && items.every((item) => item.checked)
  const checkedIds = items
    .filter((item) => item.checked && item.id)
    .map((item) => item.id as number)

  const handleCountryChange = (
    id: number | undefined,
    field: 'loadingCountry' | 'unloadingCountry',
    name: string,
  ) => {
    const country = countries.find((c) => c.name === name)
    if (country) editor.updateItem(id, { [field]: country })
  }

  return (
    <>
      <TmsSourceFilter
        companyId={companyId}
        name="tms-source"
        driverName={driverName}
        dateFrom={editor.dateFrom}
        dateTo={editor.dateTo}
        onDateFromChange={editor.setDateFrom}
        onDateToChange={editor.setDateTo}
        mode={mode}
        onModeChange={editor.setMode}
        vehicleId={editor.vehicleId}
        onVehicleChange={editor.setVehicleId}
      />

      <div className="tw-flex-1 tw-overflow-y-auto tw-bg-white tw-px-6 tw-py-4">
        {items.length > 0 && (
          <div className="tw-mb-3 tw-flex tw-items-center">
            <button
              type="button"
              disabled={checkedIds.length === 0}
              onClick={() => editor.requestRemoval(checkedIds)}
              className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm tw-text-content-base hover:tw-bg-base-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
            >
              <FaIcon icon={faTrash} />
              {t('operation.delete-rows')}
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <span>{t('common.loading')}</span>
          </div>
        ) : items.length === 0 ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
            <span>{t('businessTrip.no-data')}</span>
          </div>
        ) : (
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full">
              <TableHeader
                columns={getColumns(t)}
                toggleAll={editor.toggleAll}
                isSelectedAny={allChecked}
                sortConfig={null}
              />
              <tbody>
                {items.map((item, index) => (
                  <tr
                    key={item.id || index}
                    className="tw-border-b tw-border-white"
                    style={{ backgroundColor: getRowBackground(item.routeType) }}
                  >
                    <td className="tw-px-4 tw-py-2">
                      <Checkbox
                        id={`tms_${item.id || index}`}
                        checked={item.checked || false}
                        onChange={(e) => editor.toggleItem(item.id, e.target.checked)}
                      />
                    </td>
                    <td className="tw-px-2 tw-py-2">
                      <input
                        type="text"
                        value={item.docNumber ?? ''}
                        onChange={(e) => editor.updateItem(item.id, { docNumber: e.target.value })}
                        className="tw-w-full tw-min-w-[110px] tw-rounded tw-border tw-border-base-300 tw-bg-white tw-px-2 tw-py-1 tw-text-sm tw-text-content-base focus:tw-border-primary focus:tw-outline-none"
                      />
                    </td>
                    <td className="tw-px-2 tw-py-2">
                      <TmsDateTimeInput
                        value={item.loadingDate}
                        onChange={(date) => editor.updateItem(item.id, { loadingDate: date })}
                      />
                    </td>
                    <td className="tw-min-w-[160px] tw-px-2 tw-py-2">
                      <Dropdown
                        compact
                        searchable
                        portalMenu
                        options={countryOptions}
                        value={item.loadingCountry?.name ?? null}
                        placeholder={item.loadingCountry?.name || '-'}
                        searchPlaceholder={t('menu.search')}
                        triggerClassName="tw-bg-white"
                        onChange={(name) => handleCountryChange(item.id, 'loadingCountry', name)}
                      />
                    </td>
                    <td className="tw-px-2 tw-py-2">
                      <TmsDateTimeInput
                        value={item.unloadingDate}
                        onChange={(date) => editor.updateItem(item.id, { unloadingDate: date })}
                      />
                    </td>
                    <td className="tw-min-w-[160px] tw-px-2 tw-py-2">
                      <Dropdown
                        compact
                        searchable
                        portalMenu
                        options={countryOptions}
                        value={item.unloadingCountry?.name ?? null}
                        placeholder={item.unloadingCountry?.name || '-'}
                        searchPlaceholder={t('menu.search')}
                        triggerClassName="tw-bg-white"
                        onChange={(name) => handleCountryChange(item.id, 'unloadingCountry', name)}
                      />
                    </td>
                    <TableTextRow>{item.vehicle?.registrationNumber || '-'}</TableTextRow>
                    <TableTextRow>{t(getTmsRouteTypeName(item.routeType)) || '-'}</TableTextRow>
                    <td className="tw-px-2 tw-py-2">
                      {item.id ? (
                        <button
                          type="button"
                          title={t('tmsDownload.remove-row')}
                          onClick={() => editor.requestRemoval([item.id as number])}
                          className="tw-rounded tw-p-1.5 tw-text-content-base hover:tw-bg-white/60"
                        >
                          <FaIcon icon={faTrash} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={editor.pendingRemoval !== null}
        onClose={editor.cancelRemoval}
        onConfirm={editor.confirmRemoval}
        title={t('tmsDownload.remove-confirm-title')}
        text={t('tmsDownload.remove-confirm')}
        actionText={t('common.delete')}
      />
    </>
  )
}

export default TmsLoadsEditor
